import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { Stack } from '@/components/layout';
import { Button, FormInput, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import type { InviteGuardianForm } from '@/features/guardian-player/schemas/invite-guardian-schema';
import { MAX_INVITE_MESSAGE_LENGTH } from '@/features/guardian-player/schemas/invite-guardian-schema';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import { MAX_EMAIL_LENGTH } from '@/validation';

import InviteInfoCard from './invite-info-card';
import RelationshipSelector, { type RelationshipOption } from './relationship-selector';

const strings = STRINGS.guardianPlayer.inviteGuardian;

export type InviteGuardianContentProps = {
  screenTitle: string;
  playerName: string;
  avatarUrl?: string;
  detailLine: string;
  sinceLine: string;
  benefits: string[];
  roleOptions: RelationshipOption[];
  control: Control<InviteGuardianForm>;
  onBack: () => void;
  onSubmit: () => void;
  /** Disables the submit button while the invitation request is in flight. */
  submitting?: boolean;
};

export default function InviteGuardianContent({
  screenTitle,
  avatarUrl,
  playerName,
  detailLine,
  sinceLine,
  benefits,
  roleOptions,
  control,
  onBack,
  onSubmit,
  submitting = false,
}: InviteGuardianContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.root}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        containerProps={!isTablet ? { backgroundColor: 'transparent' } : undefined}
      />

      <KeyboardAwareScrollView
        style={styles.keyboardAvoidingView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}>
        <Stack
          flex={1}
          width="100%"
          maxWidth={isTablet ? scale(380) : undefined}
          alignSelf="center">
          <Stack gap={scale(24)}>
            {/* Relationship */}
            <Controller
              control={control}
              name="role"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Stack gap={scale(12)}>
                  <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.label}>
                    {strings.relationshipLabel}
                  </Text>
                  <RelationshipSelector
                    options={roleOptions}
                    value={value || null}
                    onChange={onChange}
                  />
                  {error?.message ? (
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.errorText}>
                      {error.message}
                    </Text>
                  ) : null}
                </Stack>
              )}
            />

            {/* Email */}
            <Controller
              control={control}
              name="email"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <FormInput
                  label={strings.emailLabel}
                  required
                  uppercaseLabel={false}
                  labelVariant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
                  inputVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  labelGap={scale(4)}
                  labelStyle={styles.fieldLabel}
                  inputStyle={styles.fieldText}
                  contentStyle={styles.fieldContent}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.emailPlaceholder}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  trim
                  maxLength={MAX_EMAIL_LENGTH}
                />
              )}
            />

            <InviteInfoCard
              name={playerName}
              detailLine={detailLine}
              sinceLine={sinceLine}
              benefits={benefits}
              avatarUrl={avatarUrl}
            />

            {/* Message */}
            <Controller
              control={control}
              name="message"
              render={({ field: { value, onChange, onBlur } }) => (
                <FormInput
                  label={strings.messagePlaceholder}
                  hideLabel
                  inputVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  inputStyle={styles.messageText}
                  contentStyle={styles.messageContent}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeholder={strings.messagePlaceholder}
                  multiline
                  numberOfLines={4}
                  maxLength={MAX_INVITE_MESSAGE_LENGTH}
                />
              )}
            />
          </Stack>

          <Stack flex={1} minHeight={scale(20)} />

          <Button onPress={onSubmit} disabled={submitting} style={styles.submitButton}>
            <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
              {strings.submit}
            </Button.Text>
          </Button>
        </Stack>
      </KeyboardAwareScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: scale(20),
    paddingTop: scale(13),
    paddingBottom: scale(24),
    gap: scale(20),
  },
  label: {
    color: guardianColors['10'],
  },
  errorText: {
    color: guardianColors.Error,
  },
  fieldLabel: {
    color: guardianColors['10'],
  },
  fieldText: {
    color: guardianColors['10'],
    marginLeft: 0,
  },
  fieldContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
  },
  messageContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(100),
    alignItems: 'flex-start',
    paddingLeft: scale(12),
    paddingRight: scale(12),
    paddingVertical: scale(8),
  },
  messageText: {
    color: guardianColors['10'],
    marginLeft: 0,
    height: '100%',
    textAlignVertical: 'top',
  },
  submitButton: {
    width: '100%',
    borderRadius: scale(12),
    backgroundColor: guardianColors['Main/Main'],
  },
}));
